import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { testimonials } from '@/mocks/data';

function ContactForm() {
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [statusMsg, setStatusMsg] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="phone_alt"]') as HTMLInputElement)?.value.trim();
    if (honeypot) {
      setStatus('success');
      setStatusMsg('提交成功！我们会尽快与您联系。');
      setFormData({ name: '', email: '', subject: '', message: '' });
      return;
    }

    if (formData.message.length > 500) {
      setStatus('error');
      setStatusMsg('留言内容不能超过500个字符。');
      return;
    }

    try {
      const fd = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9pmhu1n2trhb35gvhv0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(fd as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setStatus('success');
        setStatusMsg('提交成功！感谢您的留言，我们会尽快与您联系。');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setStatus('error');
        setStatusMsg(serverMsg.includes('spam') ? '提交失败，请稍后重试。' : (serverMsg || '提交失败，请稍后重试。'));
      }
    } catch {
      setStatus('error');
      setStatusMsg('网络错误，请检查网络连接后重试。');
    }
  };

  return (
    <form onSubmit={handleSubmit} data-readdy-form className="space-y-5">
      <input
        type="text"
        name="phone_alt"
        className="bot-field"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />
      <div>
        <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
          姓名
        </label>
        <input
          id="contact-name"
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-colors"
          placeholder="请输入您的姓名"
        />
      </div>
      <div>
        <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
          邮箱
        </label>
        <input
          id="contact-email"
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          required
          className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-colors"
          placeholder="请输入您的邮箱地址"
        />
      </div>
      <div>
        <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
          主题
        </label>
        <input
          id="contact-subject"
          type="text"
          name="subject"
          value={formData.subject}
          onChange={handleChange}
          required
          className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-colors"
          placeholder="请输入留言主题"
        />
      </div>
      <div>
        <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
          留言内容
        </label>
        <textarea
          id="contact-message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          required
          rows={5}
          maxLength={500}
          className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-colors resize-none"
          placeholder="请输入您的留言内容（500字以内）"
        />
        <p className="text-xs text-foreground-400 mt-1 text-right">{formData.message.length}/500</p>
      </div>
      <button
        type="submit"
        className="w-full py-3 rounded-lg bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
      >
        提交留言
      </button>
      {status !== 'idle' && (
        <p className={`text-sm text-center ${status === 'success' ? 'text-secondary-600' : 'text-red-500'}`}>
          {statusMsg}
        </p>
      )}
    </form>
  );
}

const statsData = [
  { value: '500万+', label: '注册用户' },
  { value: '10万+', label: '正版资源' },
  { value: '99.9%', label: '服务可用率' },
  { value: '8年', label: '稳定运营' },
];

const teamData = [
  {
    name: '内容运营团队',
    icon: 'ri-team-line',
    desc: '由资深内容编辑组成的专业运营团队，确保每日高质量内容更新，覆盖全品类资源。',
  },
  {
    name: '技术研发团队',
    icon: 'ri-code-s-slash-line',
    desc: '来自一线互联网公司的技术骨干，持续优化平台性能，保障极速加载与稳定访问。',
  },
  {
    name: '版权合作团队',
    icon: 'ri-shake-hands-line',
    desc: '与国内外多家内容提供商建立长期合作关系，确保所有资源均为正版授权。',
  },
  {
    name: '用户服务团队',
    icon: 'ri-customer-service-2-line',
    desc: '7×24小时在线客服支持，快速响应用户问题，提供专业、贴心的服务体验。',
  },
];

const contactInfo = [
  { icon: 'ri-mail-line', label: '电子邮箱', value: 'contact@91heaven.com' },
  { icon: 'ri-phone-line', label: '客服热线', value: '400-888-9191' },
  { icon: 'ri-time-line', label: '工作时间', value: '周一至周日 9:00 - 21:00' },
  { icon: 'ri-map-pin-line', label: '公司地址', value: '北京市朝阳区建国路88号SOHO现代城A座' },
];

export default function AboutPage() {
  return (
    <>
      <Navbar />
      <main className="pt-16">
        {/* Breadcrumb */}
        <div className="bg-background-50 border-b border-background-200">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />
            <div className="pb-6">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
                关于91天堂
              </h1>
              <p className="text-sm text-foreground-500 max-w-2xl">
                致力于为用户提供海量正版资源、极速加载体验与安全稳定的访问环境
              </p>
            </div>
          </div>
        </div>

        {/* Brand Introduction */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
              <div className="flex-1">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                  品牌介绍
                </h2>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                  91天堂成立于2018年，是国内领先的正版内容聚合平台。我们始终秉持"内容为王、用户至上"的核心理念，
                  致力于为广大华语用户提供海量正版、高清流畅的视听内容体验。
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                  经过多年的深耕发展，91天堂已经建立起涵盖电影、电视剧、综艺、动漫、纪录片、体育、音乐、游戏等
                  八大内容品类的完整资源矩阵。平台拥有超过10万部正版授权资源，累计服务超过500万注册用户。
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  我们与国内外多家顶级内容提供商建立了战略合作关系，确保所有资源均为合法授权。同时，平台采用
                  全球CDN加速技术与先进的视频编码方案，为用户提供极速加载与超清画质的双重保障。
                </p>
              </div>
              <div className="flex-1 w-full">
                <div className="rounded-xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20open%20office%20workspace%20with%20large%20screens%20displaying%20content%2C%20collaborative%20team%20environment%2C%20warm%20ambient%20lighting%2C%20creative%20tech%20company%20atmosphere%2C%20minimal%20clean%20design%2C%20professional%20yet%20relaxed%20vibe%2C%20indoor%20plants%2C%20natural%20wood%20desks&width=800&height=500&seq=about-brand&orientation=landscape"
                    alt="91天堂办公环境"
                    className="w-full object-cover"
                    style={{ aspectRatio: '16/10' }}
                  />
                </div>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mt-14">
              {statsData.map((stat, idx) => (
                <div
                  key={idx}
                  className="text-center p-6 rounded-xl bg-background-100 border border-background-200"
                >
                  <p className="text-2xl md:text-3xl font-heading font-bold text-primary-500 mb-1">
                    {stat.value}
                  </p>
                  <p className="text-sm text-foreground-500">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Team */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-3">
                专业团队
              </h2>
              <p className="text-sm md:text-base text-foreground-500 max-w-xl mx-auto">
                由行业精英组成的四大核心团队，为您的体验保驾护航
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {teamData.map((team, idx) => (
                <div
                  key={idx}
                  className="p-6 rounded-xl bg-background-50 border border-background-200 hover:border-primary-200 transition-all duration-300"
                >
                  <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-accent-100 text-accent-500 mb-4">
                    <i className={`${team.icon} text-xl`}></i>
                  </div>
                  <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2">
                    {team.name}
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    {team.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section id="testimonials" className="py-14 md:py-20 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-3">
                用户口碑
              </h2>
              <p className="text-sm md:text-base text-foreground-500 max-w-xl mx-auto">
                来自真实用户的评价，是我们不断前进的动力
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {testimonials.map((t) => (
                <div
                  key={t.id}
                  className="p-5 rounded-xl bg-background-100 border border-background-200"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: t.rating }, (_, i) => (
                      <i key={i} className="ri-star-fill text-accent-400 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    {t.content}
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0">
                      <img src={t.avatar} alt={t.name} className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">{t.name}</p>
                      <p className="text-xs text-foreground-400">{t.date}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section id="contact" className="py-14 md:py-20 bg-background-100">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-3">
                联系我们
              </h2>
              <p className="text-sm md:text-base text-foreground-500 max-w-xl mx-auto">
                有任何问题或建议？欢迎随时与我们联系
              </p>
            </div>
            <div className="flex flex-col lg:flex-row gap-10 max-w-5xl mx-auto">
              {/* Contact Info */}
              <div className="flex-1 space-y-5">
                {contactInfo.map((info, idx) => (
                  <div key={idx} className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500 flex-shrink-0">
                      <i className={`${info.icon} text-lg`}></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400 mb-0.5">{info.label}</p>
                      <p className="text-sm font-medium text-foreground-800">{info.value}</p>
                    </div>
                  </div>
                ))}

                {/* Map */}
                <div className="mt-6 rounded-xl overflow-hidden border border-background-200">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d12213.765443253463!2d116.4633!3d39.9087!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x35f0522e7e3e55cf%3A0x147d1918a499af5b!2sSOHO%20%E7%8E%B0%E4%BB%A3%E5%9F%8E!5e0!3m2!1szh-CN!2s!4v1691234567890"
                    width="100%"
                    height="200"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="91天堂公司位置"
                  />
                </div>
              </div>

              {/* Contact Form */}
              <div className="flex-1">
                <div className="p-6 rounded-xl bg-background-50 border border-background-200">
                  <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-5">
                    在线留言
                  </h3>
                  <ContactForm />
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      {/* Schema.org */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于91天堂',
            description: '91天堂是深受用户喜爱的内容平台，提供海量正版资源、极速加载体验与安全稳定的访问环境。',
            url: typeof window !== 'undefined' ? window.location.href : '',
            about: {
              '@type': 'Organization',
              name: '91天堂',
              description: '国内领先的正版内容聚合平台，涵盖电影、电视剧、综艺、动漫等八大内容品类，拥有超过10万部正版授权资源和500万注册用户。',
              foundingDate: '2018',
              contactPoint: {
                '@type': 'ContactPoint',
                telephone: '+86-400-888-9191',
                contactType: 'customer service',
                availableLanguage: ['Chinese'],
              },
              address: {
                '@type': 'PostalAddress',
                streetAddress: '建国路88号SOHO现代城A座',
                addressLocality: '北京',
                addressRegion: '朝阳区',
                addressCountry: 'CN',
              },
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: typeof window !== 'undefined' ? window.location.origin + '/' : '/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: '关于我们',
              },
            ],
          }),
        }}
      />
    </>
  );
}